import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useParams } from 'react-router-dom';
import { LanguageProvider, useLanguage, Language } from './contexts/LanguageContext';
import { NoticeBar } from './components/NoticeBar';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { FloatingContact } from './components/ContactButtons';
import { Home } from './pages/Home';
import { Contact } from './pages/Contact';
import { PricingPage } from './pages/PricingPage';
import { HowItWorksPage } from './pages/HowItWorksPage';
import { FAQPage } from './pages/FAQPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { RefundPolicyPage } from './pages/RefundPolicyPage';
import { GraciasPage } from './pages/GraciasPage';
import { GuiaPage } from './pages/GuiaPage';
import { PedidoPage } from './pages/PedidoPage';
import { FotoPage } from './pages/FotoPage';
import { RevisionPage } from './pages/RevisionPage';
import { AppointmentPage } from './pages/AppointmentPage';

// Whenever the route (page) changes, jump back to the top. Without this, a SPA
// keeps the previous scroll position, so a new page opens scrolled down instead
// of at its top. We reset both window and the scrolling element to be safe
// across browsers.
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
  }, [pathname]);
  return null;
}

// Travelpayouts "Drive" (afiliados de viaje). El script de un tercero se carga SOLO en la
// portada, donde está la franja "Planee su viaje". Nunca debe correr en las páginas donde el
// cliente escribe datos personales (pedido, pago, foto, revisión, gracias). Como el sitio es
// una sola página (SPA), un script ya cargado no se puede descargar; por eso, si el cliente
// pasa de la portada a una página sensible, se recarga la página completa para empezar
// limpia y sin el script.
const TP_SRC = 'https://emrld.ltd/NTg3MDcx.js?t=587071';
const TP_ALLOWED = ['/'];
const TP_SENSITIVE = ['/pedido', '/gracias', '/foto', '/revision', '/appointment'];
let tpLoaded = false;
function TravelpayoutsLoader() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : '/';
    if (tpLoaded && TP_SENSITIVE.includes(path)) {
      window.location.reload();
      return;
    }
    if (!tpLoaded && TP_ALLOWED.includes(path)) {
      try {
        const s = document.createElement('script');
        s.async = true;
        s.setAttribute('data-cmp-ab', '2');
        s.src = TP_SRC;
        document.head.appendChild(s);
        tpLoaded = true;
      } catch {
        /* sin el script el sitio funciona igual */
      }
    }
  }, [pathname]);
  return null;
}

// PRUEBA (2026-10-18): direcciones propias de Noor que redirigen al enlace de afiliado de
// Travelpayouts. Ejemplo: noorpassportservices.com/viaje/seguro. El navegador del cliente
// termina pasando por el enlace tpo.lu, que es el que registra el clic. Las tarjetas de la
// portada TODAVÍA usan los enlaces tpo.lu directos; se cambian solo después de confirmar en
// Travelpayouts (Reports) que estos clics se registran.
const GO_LINKS: Record<string, string> = {
  vuelos: 'https://kiwi.tpo.lu/aC11WFE8',
  esim: 'https://airalo.tpo.lu/vsFqn4Kv',
  seguro: 'https://ektatraveling.tpo.lu/FTOuQPfI',
  autos: 'https://getrentacar.tpo.lu/hCX2Q4IK'
};
function GoRedirect() {
  const {
    slug
  } = useParams();
  const target = slug ? GO_LINKS[slug.toLowerCase()] : undefined;
  useEffect(() => {
    const m = document.createElement('meta');
    m.name = 'robots';
    m.content = 'noindex, nofollow';
    document.head.appendChild(m);
    if (target) window.location.replace(target);
    return () => {
      m.remove();
    };
  }, [target]);
  return <div className="max-w-xl mx-auto px-4 py-20 text-center" data-id="element-0" data-mp="anwjbhc9qqk3">
      {target ? <p className="text-brand-textPrimary" data-id="element-1" data-mp="qyqahmwzo57t">
          Redirecting…{' '}
          <a className="underline" href={target} rel="sponsored noopener noreferrer" data-id="element-2" data-mp="m5dzifl8fwxj">
            Continue
          </a>
        </p> : <p className="text-brand-textPrimary" data-id="element-3" data-mp="h59ecc9dqe0z">
          <a className="underline" href="/" data-id="element-4" data-mp="c90x8tbmgar6">
            Noor Passport Services
          </a>
        </p>}
    </div>;
}

// Cada página pública tiene su propio título ("Precios | Noor Passport Services").
// Así Google puede mostrar cada página con un nombre claro debajo del resultado de
// la marca. La portada y las páginas internas muestran solo "Noor Passport Services".
const BRAND = 'Noor Passport Services';
const PAGE_TITLES: Record<string, Record<Language, string>> = {
  '/how-it-works': {
    en: 'How it works',
    es: 'Cómo funciona',
    ht: 'Kijan li mache',
    vi: 'Cách hoạt động'
  },
  '/pricing': {
    en: 'Packages & pricing',
    es: 'Paquetes y precios',
    ht: 'Pakè ak pri',
    vi: 'Gói và bảng giá'
  },
  '/faq': {
    en: 'Frequently asked questions',
    es: 'Preguntas frecuentes',
    ht: 'Kesyon moun poze souvan',
    vi: 'Câu hỏi thường gặp'
  },
  '/contact': {
    en: 'Contact',
    es: 'Contacto',
    ht: 'Kontak',
    vi: 'Liên hệ'
  },
  '/guia': {
    en: 'Which form do I need?',
    es: '¿Qué formulario necesito?',
    ht: 'Ki fòm mwen bezwen?',
    vi: 'Tôi cần mẫu đơn nào?'
  },
  '/privacy-policy': {
    en: 'Privacy policy',
    es: 'Política de privacidad',
    ht: 'Politik vi prive',
    vi: 'Chính sách quyền riêng tư'
  },
  '/terms-of-service': {
    en: 'Terms of service',
    es: 'Términos de servicio',
    ht: 'Kondisyon sevís',
    vi: 'Điều khoản dịch vụ'
  },
  '/refund-policy': {
    en: 'Refund policy',
    es: 'Política de reembolsos',
    ht: 'Politik ranbousman',
    vi: 'Chính sách hoàn tiền'
  }
};
function PageTitle() {
  const {
    pathname
  } = useLocation();
  const {
    language
  } = useLanguage();
  useEffect(() => {
    const key = pathname.length > 1 ? pathname.replace(/\/+$/, '') : '/';
    const p = PAGE_TITLES[key];
    document.title = p ? `${p[language]} | ${BRAND}` : BRAND;
  }, [pathname, language]);
  return null;
}

// "Skip to main content" link — the first thing a keyboard / screen-reader user
// reaches. It stays visually hidden until focused, then jumps past the nav
// straight to the page content.
const SKIP_LABEL: Record<Language, string> = {
  en: 'Skip to main content',
  es: 'Saltar al contenido principal',
  ht: 'Ale dirèkteman nan kontni prensipal la',
  vi: 'Chuyển đến nội dung chính'
};
function SkipLink() {
  const {
    language
  } = useLanguage();
  return <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-full focus:bg-brand-textPrimary focus:text-white focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2" data-id="element-5" data-mp="2fqtugjh5cqaz">
      {SKIP_LABEL[language]}
    </a>;
}
export function App() {
  return <LanguageProvider data-id="element-6" data-mp="2yj5z5cv9w3oa">
      <BrowserRouter data-id="element-7" data-mp="2yfqgk02e4ci4">
        <ScrollToTop data-id="element-8" data-mp="3i3mu2ipdol3m" />
        <PageTitle data-id="element-9" data-mp="3i3d4uxc5qhyz" />
        <TravelpayoutsLoader data-id="element-10" data-mp="3i4xz0luk7ywb" />
        <div className="min-h-screen flex flex-col bg-brand-bg font-sans text-brand-textPrimary" data-id="element-11" data-mp="3i1jup82c5fm8">
          <SkipLink data-id="element-12" data-mp="3n0n1awxyrtwb" />
          {/* Legal disclosure: shown at the very top of the page but allowed to
              scroll away normally — it is NOT pinned, so it doesn't eat the
              screen (especially on mobile). */}
          <NoticeBar data-id="element-13" data-mp="2zdexykk3iuwx" />
          {/* Only the compact header stays pinned at the top while scrolling. */}
          <div className="sticky top-0 z-50" data-id="element-14" data-mp="344dhdt0ds94e">
            <Header data-id="element-15" data-mp="3456fk5l9xqax" />
          </div>
          <main id="main-content" tabIndex={-1} className="flex-grow focus:outline-none" data-id="element-16" data-mp="2pez3q8prv5j7">
            <Routes data-id="element-17" data-mp="2uf3ls36bw8bj">
              <Route path="/" element={<Home data-id="element-19" data-mp="2ubpj0ttr5x3k" />} data-id="element-18" data-mp="2uebsiz07emof" />
              <Route path="/how-it-works" element={<HowItWorksPage data-id="element-21" data-mp="2geswv5okx3t0" />} data-id="element-20" data-mp="2gdr9q2uagqux" />
              <Route path="/pricing" element={<PricingPage data-id="element-23" data-mp="2kge7slhjz80k" />} data-id="element-22" data-mp="2ggn8tuhvhlky" />
              <Route path="/faq" element={<FAQPage data-id="element-25" data-mp="2khp8udjm61nz" />} data-id="element-24" data-mp="2kfctm858q5em" />
              <Route path="/contact" element={<Contact data-id="element-27" data-mp="30u2hue5n1zfl" />} data-id="element-26" data-mp="30vx2rsgwtrjo" />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage data-id="element-29" data-mp="2vvr7m2bflmwu" />} data-id="element-28" data-mp="30tav7su0rc1g" />
              <Route path="/terms-of-service" element={<TermsOfServicePage data-id="element-31" data-mp="2qwejzfcqkbt7" />} data-id="element-30" data-mp="2vwtxc6x0pjb6" />
              <Route path="/refund-policy" element={<RefundPolicyPage data-id="element-33" data-mp="2lyml21dobpfi" />} data-id="element-32" data-mp="2qz0rxg3jgtby" />
              <Route path="/gracias" element={<GraciasPage data-id="element-35" data-mp="2m0h75j0olnl6" />} data-id="element-34" data-mp="2lxl45g9z88pv" />
              <Route path="/guia" element={<GuiaPage data-id="element-37" data-mp="2hy2mctdgcdr9" />} data-id="element-36" data-mp="2hwrhsrc6n1z3" />
              <Route path="/pedido" element={<PedidoPage data-id="element-39" data-mp="2d59f9yuigtc0" />} data-id="element-38" data-mp="2hyceami24uzl" />
              <Route path="/foto" element={<FotoPage data-id="element-41" data-mp="2d3q0kcoi6qcc" />} data-id="element-40" data-mp="2d5154eprw24i" />
              <Route path="/revision" element={<RevisionPage data-id="element-43" data-mp="287hq3cl93lbi" />} data-id="element-42" data-mp="286gcb0cuokq3" />
              <Route path="/appointment" element={<AppointmentPage data-id="element-45" data-mp="23854swa12mb1" />} data-id="element-44" data-mp="284lsxqh890o4" />
              <Route path="/viaje/:slug" element={<GoRedirect data-id="element-47" data-mp="235sdvtmal1lu" />} data-id="element-46" data-mp="238y5bfie8fkq" />
            </Routes>
          </main>
          <Footer data-id="element-48" data-mp="1y80nj41apnjr" />
          {/* Always-visible Call + WhatsApp buttons */}
          <FloatingContact data-id="element-49" data-mp="1u7zvggl9xdu8" />
        </div>
      </BrowserRouter>
    </LanguageProvider>;
}